import { Fragment, useCallback, useMemo, useState } from "react";
import { calcSelectedOptionIds } from "src/pages/configurator/calcSelectedOptionIds";
import {
  ConfiguratorStepper,
  ImageDisplay,
  ImageMosaicDisplay,
  type ImageMosaicTile,
  ModalWrapper,
  StepWrapper,
  UserWarning,
} from "src/pages/configurator/components";
import {
  ComparePackageOptionsContent,
  ExpiredOptionsSwitch,
  OptionListContainer,
  OptionListItem,
} from "src/pages/configurator/components/options";
import { useConfiguratorContext } from "src/pages/configurator/Configurator.context";
import { useComputed } from "src/utils/hooks/useComputed";
import {
  AccordionOptionCard,
  Checkbox,
  FlexibleOptionCard,
  getSelectedAduOptionCodes,
  SelectableOptionCard,
} from "~components";
import { Css } from "~generated/css";
import { CheckoutConfigOptionGroupStep, GlobalOptionType } from "~generated/graphqlTypes";
import {
  fail,
  formatAmount,
  getPlanBathroomRangeString,
  getPlanBedroomRangeString,
  getPlanSqftRangeString,
  TestIds,
  useTestIds,
} from "~utils";
import { getAduImages, getCutoffDaysRemaining, optionGroupFilter } from "../Configurator.utils";
import { AduStep_PlanListingFragment } from "./Adu.step.generated";
import { InteriorStep_CheckoutOptionGroupFragment } from "./Interior.step.generated";

export const AddAduOptionCode = "ADDADU";

enum AduOptionGroupType {
  Elevation = "elevation",
  ExteriorScheme = "exteriorScheme",
  InteriorScheme = "interiorScheme",
  SpecLevel = "specLevel",
  Personalization = "personalization",
}

export function AduStep() {
  const { hasAduSupport, checkoutConfig, goToNextStep, formState, selectedOptionIds, toggleOption, autoSaving } =
    useConfiguratorContext();
  const tid = useTestIds({}, "adu-step");
  const [loading, selectedAduPlanId] = useComputed(
    () => [formState.loading, formState.aduProductOfferingId.value],
    [formState],
  );
  const isDisabled = !checkoutConfig?.canEdit.allowed;
  const isAduEnabled = !!selectedAduPlanId;
  const [showExpiredOptions, setShowExpiredOptions] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [compareCCOG, setCompareCCOG] = useState<InteriorStep_CheckoutOptionGroupFragment | undefined>(undefined);

  // Sort buildable ADU plans by price (cheapest first)
  const sortedAduPlans = (checkoutConfig?.buildableAduPlans ?? []).sortBy((p) => p.baseConfig?.salesPriceInCents ?? 0);

  const planRenderingTiles = useMemo((): ImageMosaicTile[] => {
    return sortedAduPlans
      .map((plan) => {
        const imageUrl = plan.rendering?.previewUrl;
        if (!imageUrl) return undefined;
        return {
          id: plan.id,
          imageUrl,
          name: plan.name,
          readonly: isDisabled,
          onClick: () => formState.aduProductOfferingId.set(plan.id),
        };
      })
      .compact();
  }, [sortedAduPlans, isDisabled, formState]);

  const aduImages = getAduImages(checkoutConfig, checkoutConfig?.aduProductOffering);

  const onToggleAdu = useCallback(() => {
    if (isAduEnabled) {
      formState.aduProductOfferingId.set(null);
    } else {
      const cheapest = sortedAduPlans[0];
      if (cheapest) formState.aduProductOfferingId.set(cheapest.id);
    }
  }, [isAduEnabled, sortedAduPlans, formState]);

  // Pull OGs and check for expired
  const { aduOptionGroups, someOptionsHaveExpired } = useMemo(() => {
    const allAduOptionGroups =
      checkoutConfig?.optionGroups.filter((ccgo) => ccgo.step === CheckoutConfigOptionGroupStep.Adu) ?? [];

    const someOptionsHaveExpired = allAduOptionGroups.some(
      (ccog) => ccog.cutoffDate && getCutoffDaysRemaining(ccog.cutoffDate) <= 0,
    );

    // Only show option groups that do not have a parent (child groups are rendered inside their parent's card)
    const topLevelAduOptionGroups = allAduOptionGroups.filter((ccog) => !ccog.parentGroup);

    const aduOptionGroups = topLevelAduOptionGroups.filter(optionGroupFilter(showExpiredOptions, selectedOptionIds));

    return {
      aduOptionGroups,
      someOptionsHaveExpired,
    };
  }, [checkoutConfig?.optionGroups, selectedOptionIds, showExpiredOptions]);

  // Categorize ADU option groups into sections by type flags
  const aduGroupsByCategory = useMemo(
    () => aduOptionGroups.groupBy((ccog) => getAduGroupCategory(ccog.group.group.type)),
    [aduOptionGroups],
  );

  // passed into floor plan component to highlight selected options
  const selectedAduOptionCodes = useMemo(
    () => getSelectedAduOptionCodes(aduOptionGroups, selectedOptionIds),
    [aduOptionGroups, selectedOptionIds],
  );

  // If the plan doesn't have ADU support and we somehow ended up here, just go to the next step.
  // Guard on checkoutConfig being loaded so we don't redirect before the query resolves.
  if (!hasAduSupport && checkoutConfig) {
    goToNextStep();
    return null;
  }

  // Legacy ADU uses option-group-based configuration
  if (checkoutConfig?.buildableAduPlans?.isEmpty) {
    return <LegacyAduStep />;
  }

  return (
    <div {...tid}>
      <StepWrapper
        hideBorder
        loading={loading}
        loadingView="personalization"
        title="ADU"
        description="Add an ADU to your property for extra space or rental potential."
        left={
          !isAduEnabled ? (
            <ImageMosaicDisplay tiles={planRenderingTiles} />
          ) : (
            <ImageDisplay
              images={aduImages}
              selectedOptionCodes={selectedAduOptionCodes}
              imageIndex={selectedImageIndex}
              onClick={setSelectedImageIndex}
              {...tid.imageDisplay}
            />
          )
        }
        warningNode={
          !loading &&
          isDisabled && (
            <UserWarning title="ADU options can no longer be changed." disabledReasons={[checkoutConfig?.canEdit]} />
          )
        }
        right={
          <>
            <div css={Css.df.fdc.ba.bcGray200.br4.oh.$}>
              <AduToggle selected={isAduEnabled} onClick={onToggleAdu} disabled={isDisabled} {...tid.checkbox} />
              <AduPlanCards
                plans={sortedAduPlans}
                selectedPlanId={selectedAduPlanId ?? undefined}
                onSelect={(planId) => formState.aduProductOfferingId.set(planId)}
                disabled={isDisabled || autoSaving}
              />
            </div>
            {isAduEnabled && aduOptionGroups.nonEmpty && (
              <>
                {/* Exterior: Elevation + Exterior Scheme */}
                <AduAccordionSection
                  optionGroups={[
                    ...(aduGroupsByCategory.elevation ?? []),
                    ...(aduGroupsByCategory.exteriorScheme ?? []),
                  ]}
                  tid={tid}
                />
                {/* Interior: Interior Scheme + Spec Level */}
                <AduAccordionSection
                  optionGroups={[
                    ...(aduGroupsByCategory.interiorScheme ?? []),
                    ...(aduGroupsByCategory.specLevel ?? []),
                  ]}
                  tid={tid}
                />
                {/* Personalization: remaining options */}
                <AduOptionGroups
                  optionGroups={aduGroupsByCategory.personalization ?? []}
                  someOptionsHaveExpired={someOptionsHaveExpired}
                  showExpiredOptions={showExpiredOptions}
                  setShowExpiredOptions={setShowExpiredOptions}
                  onCompare={setCompareCCOG}
                  tid={tid}
                />
              </>
            )}
          </>
        }
      />
      <ConfiguratorStepper />
      <ModalWrapper isOpen={!!compareCCOG} title={compareCCOG?.group.name} close={() => setCompareCCOG(undefined)}>
        {compareCCOG && checkoutConfig && (
          <ComparePackageOptionsContent
            checkoutConfigId={checkoutConfig.id}
            rpogId={compareCCOG.group.id}
            ccog={compareCCOG}
            selectedOptionIds={selectedOptionIds}
            onSelect={(selectedOptionId) =>
              toggleOption(
                compareCCOG,
                compareCCOG.options.find((cco) => cco.id === selectedOptionId) ?? fail(`Missing ${selectedOptionId}`),
              )
            }
          />
        )}
      </ModalWrapper>
    </div>
  );
}

type AduOptionGroupsProps = {
  optionGroups: InteriorStep_CheckoutOptionGroupFragment[];
  someOptionsHaveExpired: boolean;
  showExpiredOptions: boolean;
  setShowExpiredOptions: (show: boolean) => void;
  onCompare: (ccog: InteriorStep_CheckoutOptionGroupFragment) => void;
  tid: TestIds;
};

function AduOptionGroups(props: AduOptionGroupsProps) {
  const { optionGroups, someOptionsHaveExpired, showExpiredOptions, setShowExpiredOptions, onCompare, tid } = props;
  if (optionGroups.isEmpty) return null;
  return (
    <>
      <PersonalizationHeader />
      {someOptionsHaveExpired && (
        <ExpiredOptionsSwitch showExpiredOptions={showExpiredOptions} setShowExpiredOptions={setShowExpiredOptions} />
      )}
      <OptionListContainer>
        {optionGroups.map((ccog) => (
          <FlexibleOptionCard
            key={ccog.group.id}
            ccfgOptionGroup={ccog}
            onCompare={(maybeChildCCOG) => onCompare(maybeChildCCOG ?? ccog)}
            {...tid.flexibleOptionCard}
          />
        ))}
      </OptionListContainer>
    </>
  );
}

function PersonalizationHeader() {
  return (
    <div css={Css.df.fdc.gapPx(12).pt4.$}>
      <div css={Css.t18.gray800.$}>Personalization</div>
      <div css={Css.t14.gray700.pb3.$}>Customize additional options for your ADU.</div>
    </div>
  );
}

type AduAccordionSectionProps = {
  optionGroups: InteriorStep_CheckoutOptionGroupFragment[];
  tid: TestIds;
};

function AduAccordionSection(props: AduAccordionSectionProps) {
  const { optionGroups, tid } = props;
  const { toggleOption, checkoutConfig, showPriceLoadingState, autoSaving, selectedOptionIds } =
    useConfiguratorContext();
  const isDisabled = !checkoutConfig?.canEdit.allowed;

  if (optionGroups.isEmpty) return null;

  return (
    <div css={Css.df.fdc.ba.bcGray200.br4.oh.mt4.$}>
      {optionGroups.map((ccog) => (
        <AccordionOptionCard
          key={ccog.group.id}
          title={ccog.group.name}
          cutoffDate={ccog.cutoffDate}
          children={
            <OptionListContainer>
              {ccog.options
                .filter((cco) => cco.isEnabled)
                .map((cco) => {
                  const { id, name, description } = cco.option;
                  const { removedOptions } = calcSelectedOptionIds(
                    checkoutConfig?.optionGroups ?? [],
                    selectedOptionIds,
                    ccog,
                    cco,
                  );
                  return (
                    <Fragment key={id}>
                      <OptionListItem
                        isRadio
                        autoSaving={showPriceLoadingState(id)}
                        optionId={id}
                        conflicts={removedOptions}
                        title={name}
                        description={description}
                        selected={selectedOptionIds.includes(id)}
                        onClick={() => toggleOption(ccog, cco)}
                        priceInCents={cco.totalPriceInCents}
                        disabled={isDisabled || autoSaving}
                        {...tid.optionListItem}
                      />
                    </Fragment>
                  );
                })}
            </OptionListContainer>
          }
          {...tid.accordionOptionCard}
        />
      ))}
    </div>
  );
}

type AduToggleProps = {
  selected: boolean;
  onClick: () => void;
  disabled: boolean;
};

function AduToggle(props: AduToggleProps) {
  const { selected, onClick, disabled } = props;
  return (
    <div css={Css.df.aic.jcsb.p2.$}>
      <span css={Css.t18.$}>Add an ADU</span>
      <Checkbox selected={selected} onClick={onClick} disabled={disabled} />
    </div>
  );
}

type AduPlanCardsProps = {
  plans: AduStep_PlanListingFragment[];
  selectedPlanId: string | undefined;
  onSelect: (planId: string) => void;
  disabled: boolean;
};

function AduPlanCards(props: AduPlanCardsProps) {
  const { plans, selectedPlanId, onSelect, disabled } = props;
  return (
    <>
      {plans.map((plan) => (
        <div key={plan.id} css={Css.bt.bcGray200.$}>
          <SelectableOptionCard
            title={plan.name}
            description={<AduPlanCardDescription plan={plan} />}
            selected={selectedPlanId === plan.id}
            onClick={() => onSelect(plan.id)}
            disabled={disabled}
          />
        </div>
      ))}
    </>
  );
}

function AduPlanCardDescription(props: { plan: AduStep_PlanListingFragment }) {
  const { plan } = props;
  return (
    <div css={Css.df.fdc.gap1.$}>
      {!!plan.baseConfig?.salesPriceInCents && (
        <div css={Css.t14.gray700.$}>{`From ${formatAmount(plan.baseConfig.salesPriceInCents)}`}</div>
      )}
      <div css={Css.df.flexWrap("wrap").colGap(3).rowGap(1).t14.gray700.$}>
        <div>{getPlanBedroomRangeString(plan)}</div>
        <div>{getPlanBathroomRangeString(plan)}</div>
        <div>{getPlanSqftRangeString(plan)}</div>
      </div>
    </div>
  );
}

function LegacyAduStep() {
  const { formState, checkoutConfig, selectedOptionIds, toggleOption, communityId } = useConfiguratorContext();
  const tid = useTestIds({}, "adu-step");
  const [compareCCOG, setCompareCCOG] = useState<InteriorStep_CheckoutOptionGroupFragment | undefined>(undefined);
  const [showExpiredOptions, setShowExpiredOptions] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const loading = useComputed(() => formState.loading, [formState]);
  const isDisabled = !checkoutConfig?.canEdit.allowed;

  const aduImages = getAduImages(checkoutConfig);

  // Pull OGs and check for expired
  const { aduOptionGroups, someOptionsHaveExpired } = useMemo(() => {
    const allAduOptionGroups =
      checkoutConfig?.optionGroups.filter((ccgo) => ccgo.step === CheckoutConfigOptionGroupStep.Adu) ?? [];

    const someOptionsHaveExpired = allAduOptionGroups.some(
      (ccog) => ccog.cutoffDate && getCutoffDaysRemaining(ccog.cutoffDate) <= 0,
    );

    // Only show option groups that do not have a parent (child groups are rendered inside their parent's card)
    const topLevelAduOptionGroups = allAduOptionGroups.filter((ccog) => !ccog.parentGroup);

    // Our option groups are already sorted and without any dedicated type sub heading to display we can just filter out the add adu group and show the rest
    const aduOptionGroups = topLevelAduOptionGroups.filter(optionGroupFilter(showExpiredOptions, selectedOptionIds));

    return {
      aduOptionGroups,
      someOptionsHaveExpired,
    };
  }, [checkoutConfig?.optionGroups, selectedOptionIds, showExpiredOptions]);

  // passed into floor plan component to highlight selected options
  const selectedAduOptionCodes = useMemo(
    () => getSelectedAduOptionCodes(aduOptionGroups, selectedOptionIds),
    [aduOptionGroups, selectedOptionIds],
  );

  // This is pretty bad. We don't want to block users from continuing but this indicates
  // a problem with our POF option setup since ADUs are always optional. So we want it fixed
  // ASAP.
  if (
    checkoutConfig &&
    !aduOptionGroups.some((ccog) => ccog.options.some((cco) => cco.option.code === AddAduOptionCode))
  ) {
    console.error(
      `Community: ${communityId}, Offering: ${checkoutConfig?.productOffering?.name}, CCFG ID:${checkoutConfig?.id}: No add ADU option group found while ADU specific options exist.`,
    );
  }
  return (
    <div {...tid.legacyAduStep}>
      <StepWrapper
        hideBorder
        loading={loading}
        loadingView="personalization"
        title="Add an ADU"
        description="Add an ADU to your property for extra space or rental potential."
        warningNode={
          !loading &&
          isDisabled && (
            <UserWarning title="ADU options can no longer be changed." disabledReasons={[checkoutConfig?.canEdit]} />
          )
        }
        left={
          <ImageDisplay
            images={aduImages}
            selectedOptionCodes={selectedAduOptionCodes}
            imageIndex={selectedImageIndex}
            onClick={setSelectedImageIndex}
            {...tid.imageDisplay}
          />
        }
        right={
          <>
            {someOptionsHaveExpired && (
              <ExpiredOptionsSwitch
                showExpiredOptions={showExpiredOptions}
                setShowExpiredOptions={setShowExpiredOptions}
              />
            )}
            <OptionListContainer>
              <>
                {/* Note: these will render a placeholder image if you're working locally.
                  That doesn't match the designs but isn't an issue in prod because these are the same cards we've been usuing in the structural step where we also don't show an image
                */}
                {aduOptionGroups.nonEmpty && (
                  <>
                    {aduOptionGroups.map((ccog) => {
                      return (
                        <FlexibleOptionCard
                          key={ccog.group.id}
                          ccfgOptionGroup={ccog}
                          onCompare={(maybeChildCCOG) => setCompareCCOG(maybeChildCCOG ?? ccog)}
                          {...tid.flexibleOptionCard}
                        />
                      );
                    })}
                  </>
                )}
              </>
            </OptionListContainer>
          </>
        }
      />
      <ConfiguratorStepper />
      <ModalWrapper isOpen={!!compareCCOG} title={compareCCOG?.group.name} close={() => setCompareCCOG(undefined)}>
        {compareCCOG && checkoutConfig && (
          <ComparePackageOptionsContent
            checkoutConfigId={checkoutConfig.id}
            rpogId={compareCCOG.group.id}
            ccog={compareCCOG}
            selectedOptionIds={selectedOptionIds}
            onSelect={(selectedOptionId) =>
              toggleOption(
                compareCCOG,
                compareCCOG.options.find((cco) => cco.id === selectedOptionId) ?? fail(`Missing ${selectedOptionId}`),
              )
            }
          />
        )}
      </ModalWrapper>
    </div>
  );
}

function getAduGroupCategory(
  type: Pick<GlobalOptionType, "isElevation" | "isExteriorScheme" | "isInteriorScheme" | "isSpecLevel">,
): AduOptionGroupType {
  if (type.isElevation) return AduOptionGroupType.Elevation;
  if (type.isExteriorScheme) return AduOptionGroupType.ExteriorScheme;
  if (type.isInteriorScheme) return AduOptionGroupType.InteriorScheme;
  if (type.isSpecLevel) return AduOptionGroupType.SpecLevel;
  return AduOptionGroupType.Personalization;
}
